import { Error } from "@mui/icons-material";
import { Box, Skeleton, Typography, styled } from "@mui/material";
import React from "react";
import { MessageProps } from "../../../interfaces/all";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import { MessageContainer } from "./styles";

const UnsupportedContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px",
  backgroundColor: "#fff3cd",
  border: "1px solid #ffeaa7",
  borderRadius: "8px",
  maxWidth: "300px",
});

const UnsupportedIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  color: "#856404",
});

const UnsupportedText = styled(Typography)({
  fontSize: "12px",
  color: "#856404",
  fontStyle: "italic",
});

interface UnsupportedMessagePropsSkeleton extends MessageProps {
  showSkeleton?: boolean;
}

const UnsupportedMessage: React.FC<UnsupportedMessagePropsSkeleton> = ({
  message,
  showAvatar = true,
  showDate = true,
  showSkeleton = false,
}) => {
  const errors = message.content.errors;
  const isFromClient = message.sentBy === "client";
  const currentReadStatus = message.readStatus || { isRead: false };

  if (showSkeleton) {
    return (
      <MessageContainer isFromClient={isFromClient} showTriangle={false}>
        {isFromClient ? (
          <>
            <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <UnsupportedContainer sx={{ pr: "62px" }}>
                  <UnsupportedIcon>
                    <Skeleton variant="circular" width={16} height={16} />
                  </UnsupportedIcon>
                  <Box>
                    <Skeleton
                      variant="text"
                      width={120}
                      height={18}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width={80} height={14} />
                  </Box>
                </UnsupportedContainer>
                <MessageMetaInfo
                  message={message}
                  showDate={showDate}
                  isFromClient={isFromClient}
                  readStatus={currentReadStatus}
                />
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <UnsupportedContainer sx={{ pr: "62px" }}>
                  <UnsupportedIcon>
                    <Skeleton variant="circular" width={16} height={16} />
                  </UnsupportedIcon>
                  <Box>
                    <Skeleton
                      variant="text"
                      width={120}
                      height={18}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width={80} height={14} />
                  </Box>
                </UnsupportedContainer>
                <MessageMetaInfo
                  message={message}
                  showDate={showDate}
                  isFromClient={isFromClient}
                  readStatus={currentReadStatus}
                />
              </Box>
            </Box>
            <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          </>
        )}
      </MessageContainer>
    );
  }

  const getErrorMessage = () => {
    if (errors && errors.length > 0) {
      const error = errors[0];
      switch (error.code) {
        case 131051:
          return "Tipo de mensagem não suportado";
        case 131050:
          return "Mensagem não pode ser entregue";
        default:
          return error.title || "Mensagem não suportada";
      }
    }

    // Fallback para tipos não implementados
    return `Tipo de mensagem "${message.type}" não implementado`;
  };

  const getErrorDetails = () => {
    if (errors && errors.length > 0) {
      const error = errors[0];
      return error.details || "Esta mensagem não pode ser exibida";
    }

    return "Este tipo de mensagem ainda não foi implementado no sistema";
  };

  return (
    <MessageContainer isFromClient={isFromClient} showTriangle={false}>
      {isFromClient ? (
        <>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          <Box
            sx={{ position: "relative", display: "flex", alignItems: "center" }}
          >
            <Box sx={{ position: "relative" }}>
              <UnsupportedContainer sx={{ pr: "62px" }}>
                <UnsupportedIcon>
                  <Error sx={{ fontSize: "16px" }} />
                </UnsupportedIcon>
                <Box>
                  <UnsupportedText
                    sx={{ fontWeight: 500, marginBottom: "2px" }}
                  >
                    {getErrorMessage()}
                  </UnsupportedText>
                  <UnsupportedText sx={{ fontSize: "11px" }}>
                    {getErrorDetails()}
                  </UnsupportedText>
                </Box>
              </UnsupportedContainer>
              <MessageMetaInfo
                message={message}
                showDate={showDate}
                isFromClient={isFromClient}
                readStatus={currentReadStatus}
              />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{ position: "relative", display: "flex", alignItems: "center" }}
          >
            <Box sx={{ position: "relative" }}>
              <UnsupportedContainer sx={{ pr: "62px" }}>
                <UnsupportedIcon>
                  <Error sx={{ fontSize: "16px" }} />
                </UnsupportedIcon>
                <Box>
                  <UnsupportedText
                    sx={{ fontWeight: 500, marginBottom: "2px" }}
                  >
                    {getErrorMessage()}
                  </UnsupportedText>
                  <UnsupportedText sx={{ fontSize: "11px" }}>
                    {getErrorDetails()}
                  </UnsupportedText>
                </Box>
              </UnsupportedContainer>
              <MessageMetaInfo
                message={message}
                showDate={showDate}
                isFromClient={isFromClient}
                readStatus={currentReadStatus}
              />
            </Box>
          </Box>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
        </>
      )}
    </MessageContainer>
  );
};

export default UnsupportedMessage;
